/* eslint-disable max-classes-per-file */

type ValidatorType = (v: string) => string

export class FormField {
  blurred: boolean = false

  errors: string[]

  validators: ValidatorType[] = []

  value: string

  constructor(initialValue: string, ...validators: ValidatorType[]) {
    this.blurred = false
    this.errors = []
    this.value = initialValue
    this.validators = validators
  }

  getValue(): string {
    return this.value
  }

  setValue(v: string): void {
    this.value = v
  }

  validate(): void {
    this.errors = []
    this.validators.forEach((validator) => {
      const res = validator(this.getValue())
      if (res) {
        this.errors.push(res)
      }
    })
  }
}

export function newFormField(
  initialValue: string, ...validators: ValidatorType[]
): FormField {
  return new FormField(initialValue, ...validators)
}

export class Validator<FieldStructure> {
  fields: Record<keyof FieldStructure, FormField>

  constructor(cfg: Record<keyof FieldStructure, FormField>) {
    this.fields = cfg
  }

  blurAll(): void {
    Object.keys(this.fields).forEach((fieldName) => {
      this.fields[fieldName].blurred = true
    })
  }

  flattenFields(): Record<keyof FieldStructure, any> {
    return Object.keys(this.fields).reduce((acc, fieldName) => ({
      ...acc,
      [fieldName]: undefined,
    }), { ...this.fields })
  }

  getData(): Record<keyof FieldStructure, string> {
    return Object.keys(this.fields).reduce((acc, fieldName) => ({
      ...acc,
      [fieldName]: this.fields[fieldName].value,
    }), this.flattenFields())
  }

  getErrors(): Record<keyof FieldStructure, string[]> {
    return Object.keys(this.fields).reduce((acc, fieldName) => ({
      ...acc,
      [fieldName]: this.fields[fieldName].errors,
    }), this.flattenFields())
  }

  getFieldErrors(fieldName: keyof FieldStructure): string[] {
    return this.fields[fieldName].errors
  }

  validate(): { valid: boolean, errors: Record<keyof FieldStructure, string[]> } {
    let valid: boolean = true
    const errs = this.flattenFields()
    Object.keys(this.fields).forEach((fieldName) => {
      this.fields[fieldName].validate()
      if (this.fields[fieldName].errors.length > 0) valid = false
      errs[fieldName] = this.fields[fieldName].errors
    })
    return {
      errors: errs,
      valid,
    }
  }

  validateField(fieldID: string, options?: { blur: boolean }): FormField {
    const field = this.fields[fieldID]
    if (options?.blur || field.blurred) {
      field.blurred = true
    }
    field.validate()
    return field
  }
}
